import { clearNotifications, restoreNotifications } from "../redux/slices/notificationSlice";
import { getCachedData, putCachedData, deleteCachedData } from "./offlineDb";

const CACHE_PREFIX = "notifications:";
const LEGACY_PREFIX = "encoaudit_notifications:";
const userId = (state) => state.auth?.isAuthenticated
  ? state.auth.user?._id || state.auth.user?.id : null;
const revisions = new WeakMap();
// Serialize reads, writes, migration and deletion so logout cannot be followed
// by an older write recreating the deleted cache.
let pending = Promise.resolve();
function enqueue(operation) {
  const result = pending.catch(() => {}).then(operation);
  pending = result.catch(() => {});
  return result;
}
function valid(snapshot) {
  return Array.isArray(snapshot?.notifications) &&
    snapshot.notifications.every(item => item && typeof item._id === "string") &&
    Number.isFinite(snapshot.unreadCount) && snapshot.unreadCount >= 0 &&
    snapshot.pagination && Number.isFinite(snapshot.lastFetched);
}
function snapshotOf(state) {
  return {
    notifications: state.notifications.slice(0, 20),
    unreadCount: state.unreadCount,
    pagination: state.pagination,
    lastFetched: state.lastFetched,
  };
}
function removeLegacy(user) {
  try { localStorage.removeItem(LEGACY_PREFIX + user); } catch {}
}

export async function restoreNotificationCache(store) {
  const user = userId(store.getState());
  if (!user) return;
  const revision = revisions.get(store.getState) || 0;
  const isCurrent = () => userId(store.getState()) === user &&
    (revisions.get(store.getState) || 0) === revision;
  try {
    const cached = await enqueue(async () => {
      let snapshot = await getCachedData(CACHE_PREFIX + user);
      if (!isCurrent()) return null;
      if (!valid(snapshot)) {
        // One-time migration preserves notifications already cached offline.
        try { snapshot = JSON.parse(localStorage.getItem(LEGACY_PREFIX + user)); } catch {}
        if (!valid(snapshot)) return null;
        snapshot = snapshotOf(snapshot);
        await putCachedData(CACHE_PREFIX + user, snapshot);
      }
      removeLegacy(user);
      return snapshotOf(snapshot);
    });
    // The network, a mutation, logout or account change may have won the race.
    if (cached && isCurrent()) store.dispatch(restoreNotifications(cached));
  } catch { /* Unavailable storage must not prevent online notifications. */ }
}

export const notificationCacheMiddleware = (store) => (next) => (action) => {
  const before = store.getState();
  const previousUser = userId(before);
  const result = next(action);
  const after = store.getState();
  const currentUser = userId(after);
  if (previousUser !== currentUser || action.type === "auth/logoutUser/pending") {
    revisions.set(store.getState, (revisions.get(store.getState) || 0) + 1);
    if (previousUser) {
      removeLegacy(previousUser);
      enqueue(() => deleteCachedData(CACHE_PREFIX + previousUser)).catch(() => {});
    }
    store.dispatch(clearNotifications());
    if (currentUser) restoreNotificationCache(store);
  } else if (currentUser && action.type !== restoreNotifications.type) {
    const previous = before.notifications;
    const current = after.notifications;
    if (current.lastFetched && (previous.notifications !== current.notifications ||
        previous.unreadCount !== current.unreadCount || previous.lastFetched !== current.lastFetched)) {
      revisions.set(store.getState, (revisions.get(store.getState) || 0) + 1);
      const snapshot = snapshotOf(current);
      enqueue(async () => {
        await putCachedData(CACHE_PREFIX + currentUser, snapshot);
        removeLegacy(currentUser);
      }).catch(() => {});
    } else if (!current.lastFetched && [
      "auth/checkAuthStatus/fulfilled", "auth/checkAuthStatus/rejected",
      "auth/getMe/fulfilled",
    ].includes(action.type)) {
      // A cached offline session is already authenticated when Redux starts,
      // so there may be no user-ID change to trigger hydration. Restore after
      // session bootstrap completes, including its offline fallback.
      restoreNotificationCache(store);
    }
  }
  return result;
};
